import BaseLayout from '@/layout/BaseLayout';
import { trpc } from '@/trpcClient';
import { CountMaterialFilterTypeWithValues } from '@shelflix/schemas/material/count';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardFilter } from './DashboardFilter';
import { SummaryDashboard, SummaryProps } from '@/components/SummaryDash';
import { toast } from 'sonner';
import { EMaterialStatus } from '@shelflix/schemas/material/enums';
import playSvg from '@/assets/icons-normalized/play.svg?raw';
import expiringSvg from '@/assets/icons-normalized/expiring.svg?raw';
import laptopSvg from '@/assets/icons-normalized/laptop.svg?raw';
import cloudOffSvg from '@/assets/icons-normalized/cloud-off.svg?raw';
import { CampaignOverview, CampaignOverviewItem } from '@/components/CampaignOverview';
import { ShortcutCard } from '@/components/ShortcutCard';
import { MaterialCatalog, MaterialCatalogItem } from '@/components/MaterialCatalog';
import { ExpiringCampaigns } from '@/components/ExpiringCampaigns';
import { GeographicDistribution } from '@/components/GeographicDistribution';

export function Dashboard() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<CountMaterialFilterTypeWithValues>({});
  const [viewState, setViewState] = useState({
    zoom: 12,
    latitude: -23.5489,
    longitude: -46.6388,
  });

  const [brandFilter, setBrandFilter] = useState('all');
  const [expirationFilter, setExpirationFilter] = useState('closest');

  const [search, setSearch] = useState('');

  const [expiringCampaignPage, setExpiringCampaignPage] = useState(1);
  const [materialsCatalogPage, setMaterialsCatalogPage] = useState(1);

  //Filters
  const {
    data: filtersData = { state: [], brand: [], material: [] },
    isLoading: _isLoadingFilters,
    isFetching: _isFetchingFilters,
    isError: _isErrorFilters,
  } = useQuery({
    ...trpc.dashboard.filters.queryOptions({
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    placeholderData: keepPreviousData,
  });

  //Data for Material Catalog
  const {
    data: countByName = { total: 0, materialsCount: [] },
    isFetching: _isLoadingCountByName,
    isError: _isErrorCountByName,
  } = useQuery({
    ...trpc.material.count.queryOptions({
      grouping: 'name',
      search: search.trim() !== '' ? search : undefined,
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
      page: materialsCatalogPage,
      recordsPerPage: 10,
      totalCount: true,
    }),
    placeholderData: keepPreviousData,
  });

  //Data for Expiring Campaigns
  const {
    data: campaignCount = { total: 0, campaignsCount: [] },
    isFetching: _isLoadingCampaignCount,
    isError: _isErrorCampaignCount,
  } = useQuery({
    ...trpc.campaign.count.queryOptions({
      brandFilter: brandFilter !== 'all' ? brandFilter : undefined,
      orderBy: expirationFilter === 'closest' ? 'asc' : 'desc',
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
      page: expiringCampaignPage,
      recordsPerPage: 10,
    }),
    placeholderData: keepPreviousData,
  });

  //Data for Summary Dashboard
  const {
    data: countByStatus = [],
    isLoading: _isLoadingCountByStatus,
    isError: _isErrorCountByStatus,
  } = useQuery({
    ...trpc.material.count.queryOptions({
      grouping: 'status',
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    placeholderData: keepPreviousData,
  });

  const statusArray = Array.isArray(countByStatus) ? countByStatus : [];
  const materialSumarryData: SummaryProps = {
    overview: {
      title: 'Total',
      amount: statusArray
        .filter(
          (item) =>
            item.group?.toLowerCase() === 'active' ||
            item.group?.toLowerCase() === 'operationalonline' ||
            item.group?.toLowerCase() === 'offline'
        )
        .reduce((sum, item) => sum + (item.count || 0), 0),
    },
    summary: [
      {
        title: 'Online',
        count: statusArray
          .filter((item) => item.group?.toLowerCase() === 'active' || item.group?.toLowerCase() === 'operationalonline')
          .reduce((sum, item) => sum + (item.count || 0), 0),
        statusFilter: 'ACTIVE',
      },
      {
        title: 'Offline',
        count: statusArray.find((item) => item.group?.toLowerCase() === 'offline')?.count || 0,
        statusFilter: 'OFFLINE',
      },
      {
        title: 'Indisponível',
        count: statusArray
          .filter((item) => item.group?.toLowerCase() === 'maintenance' || item.group?.toLowerCase() === 'standby')
          .reduce((sum, item) => sum + (item.count || 0), 0),
        statusFilter: 'MAINTENANCE',
      },
    ],
  };

  //Data for Campaign Overview
  const {
    data: schedulingOverview = { playing: 0, waiting: 0, expiring: 0, noScreen: 0 },
    isLoading: _isLoadingCountByCampaign,
    isError: _isErrorCountByCampaign,
  } = useQuery({
    ...trpc.campaign.schedulingOverview.queryOptions({
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    placeholderData: keepPreviousData,
  });

  const campaignOverviewData: CampaignOverviewItem[] = [
    {
      title: 'Executando',
      count: schedulingOverview.playing,
      color: '#12B212',
      bgColor: '#28A7451A',
      icon: playSvg,
    },
    {
      title: 'Expirando',
      count: schedulingOverview.expiring,
      color: '#FFCC66',
      bgColor: '#FFCC661A',
      icon: cloudOffSvg,
    },
    {
      title: 'Agendadas',
      count: schedulingOverview.waiting,
      color: '#33AEFF',
      bgColor: '#33AEFF1A',
      icon: expiringSvg,
    },
    {
      title: 'Sem tela',
      count: schedulingOverview.noScreen,
      color: '#CC4747',
      bgColor: '#CC47471A',
      icon: laptopSvg,
    },
  ];

  //Data for Geographic Distribution
  const {
    data: countByFacility = [],
    isLoading: _isLoadingCountByFacility,
    isError: _isErrorCountByFacility,
  } = useQuery({
    ...trpc.material.count.queryOptions({
      grouping: 'address',
      ...(filters.filter && {
        filter: [...filters.filter.map((f) => f.field)],
      }),
    }),
    placeholderData: keepPreviousData,
  });

  const {
    data: facilitiesData,
    isLoading: _isLoadingEmptyFacilities,
    isError: _isErrorEmptyFacilities,
  } = useQuery({
    ...trpc.facility.list.queryOptions({
      page: 1,
      recordsPerPage: 9999,
    }),
    placeholderData: keepPreviousData,
  });

  const location = useLocation();
  const hasShownToast = useRef(false);

  const levels = [
    { min: 11, group: 'Local', base: 10, minDist: 0.5 },
    { min: 10, group: 'Bairro', base: 10, minDist: 2 },
    { min: 8.5, group: 'Cidade', base: 10, minDist: 8 },
    { min: 7.5, group: 'Sub-região', base: 10, minDist: 32 },
    { min: 6.5, group: 'Estado', base: 10, minDist: 64 },
    { min: 5.5, group: 'Estado', base: 10, minDist: 128 },
    { min: 4.5, group: 'Região', base: 10, minDist: 256 },
    { min: 3.5, group: 'Região', base: 10, minDist: 512 },
    { min: 2.5, group: 'Região', base: 10, minDist: 1024 },
    { min: 1.5, group: 'País', base: 10, minDist: 2048 },
    { min: 0.5, group: 'País', base: 10, minDist: 4096 },
    { min: -Infinity, group: 'País', base: 20, minDist: 8192 },
  ];

  // Função para contar materiais por status em um grupo de pins
  const getStatusCounts = (pins: Array<{ materials?: Array<{ status: string; count: number }> }>) => {
    if (!pins || pins.length === 0) return [];

    const statusCounts: Record<string, number> = {};

    // Aggregate counts from all pins' materials
    pins.forEach((pin) => {
      pin.materials?.forEach((material) => {
        const status = material.status;
        if (status) {
          statusCounts[status] = (statusCounts[status] || 0) + material.count;
        }
      });
    });

    // Ordena por prioridade: OFFLINE > MAINTENANCE > ACTIVE
    const priorityOrder = [
      EMaterialStatus.OFFLINE,
      EMaterialStatus.MAINTENANCE,
      EMaterialStatus.STANDBY,
      EMaterialStatus.ACTIVE,
      EMaterialStatus.OPERATIONALONLINE,
    ];

    return Object.entries(statusCounts)
      .sort(([statusA], [statusB]) => {
        const indexA = priorityOrder.indexOf(statusA as EMaterialStatus);
        const indexB = priorityOrder.indexOf(statusB as EMaterialStatus);
        return indexA - indexB;
      })
      .map(([status, count]) => ({
        status: status as EMaterialStatus,
        count,
      }));
  };

  // Função para obter a cor de fundo do badge baseado no status
  // Função para calcular distância entre dois pontos
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Raio da Terra em km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Função para agrupar pins próximos baseado no zoom
  const groupedPins = useMemo(() => {
    const facilityArray = Array.isArray(countByFacility) ? countByFacility : [];
    if (!facilityArray || facilityArray.length === 0) return [];

    const validPins = facilityArray.filter(
      (item) =>
        (!!item.extra?.deviceLatitude && !!item.extra?.deviceLongitude) ||
        (!!item.extra?.latitude && !!item.extra?.longitude)
    );

    // CRITICAL FIX: Aggregate materials by facility FIRST
    // countByFacility has one row per material+status combination
    // We need one pin per facility, aggregating all materials within it
    type FacilityAggregation = {
      facilityName: string;
      latitude: number;
      longitude: number;
      materials: Array<{ status: string; count: number }>;
      totalCount: number;
    };

    const facilityMap: Record<string, FacilityAggregation> = {};

    validPins.forEach((pin) => {
      const facilityName = pin.group ?? '';
      const lat = Number(pin.extra?.deviceLatitude || pin.extra?.latitude) || 0;
      const lon = Number(pin.extra?.deviceLongitude || pin.extra?.longitude) || 0;
      const key = `${facilityName}_${lat}_${lon}`;

      if (!facilityMap[key]) {
        facilityMap[key] = {
          facilityName,
          latitude: lat,
          longitude: lon,
          materials: [],
          totalCount: 0,
        };
      }

      facilityMap[key].materials.push({
        status: pin.extra?.status as string,
        count: pin.count,
      });
      facilityMap[key].totalCount += pin.count;
    });

    // Convert aggregated facilities back to pin format
    const aggregatedPins = Object.values(facilityMap).map((facility) => ({
      group: facility.facilityName,
      count: facility.totalCount,
      extra: {
        latitude: facility.latitude,
        longitude: facility.longitude,
        deviceLatitude: facility.latitude,
        deviceLongitude: facility.longitude,
      },
      materials: facility.materials, // Store for status calculation
    }));

    // Add empty facilities (no materials)
    const facilitiesPins =
      facilitiesData?.facilities
        ?.filter((facility) => {
          const facilityLat = Number(facility.latitude) || 0;
          const facilityLon = Number(facility.longitude) || 0;
          const key = `${facility.name}_${facilityLat}_${facilityLon}`;
          return !facilityMap[key] && facilityLat !== 0 && facilityLon !== 0;
        })
        .map((facility) => ({
          group: facility.name,
          isGroup: false,
          groupLevel: 'individual',
          groupId: facility.name,
          centerLat: Number(facility.latitude) || 0,
          centerLon: Number(facility.longitude) || 0,
          count: 0,
          extra: {
            latitude: Number(facility.latitude) || 0,
            longitude: Number(facility.longitude) || 0,
            deviceLatitude: Number(facility.latitude) || 0,
            deviceLongitude: Number(facility.longitude) || 0,
          },
          materials: [],
        })) || [];

    if (viewState.zoom >= 15) {
      // High zoom: show individual facility pins (one per facility, not per material)
      const listPins = aggregatedPins.map((item) => {
        const pin = {
          ...item,
          isGroup: false,
          groupLevel: 'individual',
          groupId: item.group,
          centerLat: Number(item.extra.deviceLatitude || item.extra.latitude) || 0,
          centerLon: Number(item.extra.deviceLongitude || item.extra.longitude) || 0,
        };
        // Pre-calculate for individual pins
        const pinsToCheck = [item] as Array<{ materials?: Array<{ status: string; count: number }> }>;
        return {
          ...pin,
          statusCounts: item.count > 0 ? getStatusCounts(pinsToCheck) : [],
        };
      });

      return listPins.concat(
        facilitiesPins.map((f) => ({
          ...f,
          pinColor: 'text-gray-400',
          statusCounts: [],
        }))
      );
    }

    // Encontra a primeira faixa em que o zoom se encaixa
    const levelFound = levels.find((l) => viewState.zoom >= l.min);
    if (!levelFound) return [];
    const { group, base, minDist } = levelFound;
    const groupLevel = group;
    const maxDistance = Math.max(minDist, base - viewState.zoom);

    // Agrupa pins próximos
    const groups: Array<{
      centerLat: number;
      centerLon: number;
      pins: typeof aggregatedPins;
      totalCount: number;
      groupId: string;
      groupLevel: string;
    }> = [];

    aggregatedPins
      .concat(
        facilitiesPins.filter(
          (facility) =>
            facility.centerLat !== 0 &&
            facility.centerLon !== 0 &&
            !aggregatedPins.some((pin) => pin.group === facility.groupId)
        )
      )
      .forEach((pin) => {
        const pinLat = Number(pin.extra?.deviceLatitude || pin.extra?.latitude) || 0;
        const pinLon = Number(pin.extra?.deviceLongitude || pin.extra?.longitude) || 0;

        let addedToGroup = false;
        for (const group of groups) {
          const distance = calculateDistance(pinLat, pinLon, group.centerLat, group.centerLon);
          if (distance <= maxDistance) {
            group.pins.push(pin);
            group.totalCount += pin.count;
            // Recalcula o centro do grupo
            const totalLat = group.pins.reduce(
              (sum, p) => sum + Number(p.extra?.deviceLatitude || p.extra?.latitude) || 0,
              0
            );
            const totalLon = group.pins.reduce(
              (sum, p) => sum + Number(p.extra?.deviceLongitude || p.extra?.longitude) || 0,
              0
            );
            group.centerLat = totalLat / group.pins.length;
            group.centerLon = totalLon / group.pins.length;
            addedToGroup = true;
            break;
          }
        }

        if (!addedToGroup) {
          groups.push({
            centerLat: pinLat,
            centerLon: pinLon,
            pins: [pin],
            totalCount: pin.count,
            groupId: `group-${groups.length}`,
            groupLevel: groupLevel,
          });
        }
      });

    return groups.map((group) => {
      const basePin = {
        ...group.pins[0], // Usa o primeiro pin como base
        isGroup: true,
        groupLevel: group.groupLevel,
        groupId: group.groupId,
        centerLat: group.centerLat,
        centerLon: group.centerLon,
        count: group.totalCount,
        pins: group.pins,
      };
      // Pre-calculate pin color and status counts to avoid recalculation on render
      const pinsToCheck = group.pins as Array<{ materials?: Array<{ status: string; count: number }> }>;
      return {
        ...basePin,
        statusCounts: group.totalCount > 0 ? getStatusCounts(pinsToCheck) : [],
      };
    });
  }, [countByFacility, viewState.zoom, facilitiesData?.facilities]);

  useEffect(() => {
    if (location.state?.toastUnauthorized && !hasShownToast.current) {
      toast.error('Atenção você não possui permissão para acessar essa página.');
      hasShownToast.current = true;

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  return (
    <BaseLayout>
      <section className="flex w-full flex-1 flex-col items-center gap-6">
        <DashboardFilter
          states={filtersData.state}
          brands={filtersData.brand}
          materials={filtersData.material}
          onChange={setFilters}
          isLoading={_isFetchingFilters}
        />
        <div className="xxl:grid-cols-6 grid w-full gap-4 lg:grid-cols-4 xl:grid-cols-4">
          <SummaryDashboard summaryData={materialSumarryData} className="xxl:col-span-2 lg:col-span-2 xl:col-span-2" />
          <CampaignOverview
            campaignOverviewData={campaignOverviewData}
            className="xxl:col-span-2 lg:col-span-2 xl:col-span-2"
          />
          <ShortcutCard className="xxl:col-span-2 lg:col-span-1 xl:col-span-1" />
          <MaterialCatalog
            materialCatalogData={{
              total: 'total' in countByName ? countByName.total || 0 : 0,
              materialsCount:
                'materialsCount' in countByName && Array.isArray(countByName.materialsCount)
                  ? countByName.materialsCount.filter((item): item is MaterialCatalogItem => item.group !== undefined)
                  : [],
            }}
            search={search}
            onSearchChange={setSearch}
            page={materialsCatalogPage}
            onPageChange={setMaterialsCatalogPage}
            loading={_isLoadingCountByName}
            className="xxl:col-span-3 lg:col-span-3 xl:col-span-3"
          />
          <ExpiringCampaigns
            expiringCampaignsData={campaignCount}
            brandFilter={brandFilter}
            onBrandFilterChange={setBrandFilter}
            expirationFilter={expirationFilter}
            onExpirationFilterChange={setExpirationFilter}
            brandOptions={filtersData.brand}
            page={expiringCampaignPage}
            onPageChange={setExpiringCampaignPage}
            loading={_isLoadingCampaignCount}
            className="xxl:col-span-3 lg:col-span-4 xl:col-span-4"
          />
        </div>
        <GeographicDistribution groupedPins={groupedPins} setViewState={setViewState} />
      </section>
    </BaseLayout>
  );
}
